import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, CheckCircle2, ChevronRight, Download, MinusCircle, XCircle } from 'lucide-react';
import type { ImportColumnReport, ImportProblem } from './useImport';
import { cn } from '@/lib/cn';

/** Rows listed per group before "show all". */
const PREVIEW_ROWS = 20;

function csvCell(v: string): string {
  return /[",\n;]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
}

/**
 * The column check for a shapefile import: each field the importer reads,
 * the file column it matched, how full it is, and what happens when it is
 * missing. Answers "why did nothing import" before the user reads any row.
 */
export function ImportColumnCheck({
  columns,
  defaultYear,
}: {
  columns: ImportColumnReport;
  defaultYear?: number | null;
}) {
  const { t } = useTranslation(['import']);
  return (
    <div className="rounded-md border border-slate-200 dark:border-slate-700">
      <div className="border-b border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 dark:border-slate-700 dark:text-slate-200">
        {t('columns.title', { defaultValue: 'Coloanele fișierului' })}
      </div>
      <table className="w-full text-xs">
        <thead className="text-[10px] uppercase text-slate-500">
          <tr>
            <th className="px-3 py-1 text-left font-semibold">{t('columns.field', { defaultValue: 'Câmp' })}</th>
            <th className="px-3 py-1 text-left font-semibold">{t('columns.column', { defaultValue: 'Coloană găsită' })}</th>
            <th className="px-3 py-1 text-left font-semibold">{t('columns.status', { defaultValue: 'Stare' })}</th>
          </tr>
        </thead>
        <tbody>
          {columns.fields.map((f) => {
            const full = f.column !== null && f.filled === columns.rows;
            const partial = f.column !== null && f.filled > 0 && f.filled < columns.rows;
            const missing = f.column === null || f.filled === 0;
            const Icon = full || partial ? CheckCircle2 : f.requirement === 'required' ? XCircle : f.requirement === 'fallback' ? AlertTriangle : MinusCircle;
            const tone = full
              ? 'text-emerald-600'
              : partial
                ? 'text-amber-600'
                : f.requirement === 'required'
                  ? 'text-red-600'
                  : f.requirement === 'fallback'
                    ? 'text-amber-600'
                    : 'text-slate-400';
            let status: string;
            if (full) status = t('columns.ok', { defaultValue: 'completată pe toate rândurile' });
            else if (partial)
              status = t('columns.partial', {
                defaultValue: 'completată pe {{filled}} din {{rows}} rânduri',
                filled: f.filled,
                rows: columns.rows,
              });
            else if (f.requirement === 'required')
              status = t('columns.missingRequired', {
                defaultValue: 'lipsește: obligatorie, rândurile vor fi respinse. Adăugați {{expected}}',
                expected: f.expected[0],
              });
            else if (f.requirement === 'fallback')
              status = t(`columns.fallback.${f.field}`, {
                defaultValue: 'lipsește: se folosește o valoare de rezervă',
                year: defaultYear ? `(${defaultYear})` : '',
              });
            else status = t('columns.missingOptional', { defaultValue: 'lipsește (opțională)' });
            return (
              <tr key={f.field} className="border-t border-slate-100 dark:border-slate-800">
                <td className="px-3 py-1 font-medium">{t(`field.${f.field}`, { defaultValue: f.field })}</td>
                <td className="px-3 py-1 font-mono text-[11px]">
                  {f.column ?? (
                    <span className="text-slate-400" title={f.expected.join(', ')}>
                      {missing ? '—' : ''}
                    </span>
                  )}
                </td>
                <td className={cn('px-3 py-1', tone)}>
                  <span className="inline-flex items-start gap-1">
                    <Icon className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                    {status}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="border-t border-slate-200 px-3 py-1.5 text-[11px] text-slate-500 dark:border-slate-700">
        {t('columns.found', {
          defaultValue: 'Coloane în fișier: {{list}}',
          list: columns.found.length > 0 ? columns.found.join(', ') : '—',
        })}
      </div>
    </div>
  );
}

/**
 * Problems of an import, grouped by cause. Each group says what is wrong
 * and how to fix it, and lists the affected rows with the values needed to
 * find them in QGIS. The whole list downloads as CSV.
 */
export function ImportProblems({
  problems,
  rowLabel,
  fileName = 'probleme-import.csv',
}: {
  problems: ImportProblem[];
  /** How a problem's position reads: "Rândul 12" or an archive name. */
  rowLabel?: (p: ImportProblem) => string;
  fileName?: string;
}) {
  const { t } = useTranslation(['import']);
  const [open, setOpen] = useState<Set<string>>(() => new Set());
  const [showAll, setShowAll] = useState<Set<string>>(() => new Set());

  const groups = useMemo(() => {
    const m = new Map<string, { code: string; items: ImportProblem[]; fields: Set<string> }>();
    for (const p of problems) {
      const g = m.get(p.code) ?? { code: p.code, items: [], fields: new Set<string>() };
      g.items.push(p);
      for (const f of p.fields ?? []) g.fields.add(f);
      m.set(p.code, g);
    }
    return [...m.values()].sort((a, b) => b.items.length - a.items.length);
  }, [problems]);

  if (problems.length === 0) return null;
  const label =
    rowLabel ?? ((p: ImportProblem) => t('problems.row', { defaultValue: 'Rândul {{n}}', n: p.rawIndex + 1 }));

  const download = () => {
    const ctxKeys = [...new Set(problems.flatMap((p) => Object.keys(p.context ?? {})))];
    const header = ['rand', 'cod', 'motiv', ...ctxKeys];
    const lines = [header.join(',')].concat(
      problems.map((p) =>
        [label(p), p.code, p.reason, ...ctxKeys.map((k) => p.context?.[k] ?? '')].map(csvCell).join(','),
      ),
    );
    const blob = new Blob(['﻿' + lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(url);
  };

  const toggle = (set: Set<string>, key: string) => {
    const next = new Set(set);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    return next;
  };

  return (
    <div className="rounded-md border border-red-200 dark:border-red-900/50">
      <div className="flex items-center justify-between gap-2 border-b border-red-200 bg-red-50 px-3 py-1.5 dark:border-red-900/50 dark:bg-red-900/20">
        <span className="text-xs font-semibold text-red-800 dark:text-red-300">
          {t('problems.title', { defaultValue: 'Probleme ({{count}} rânduri)', count: problems.length })}
        </span>
        <button
          type="button"
          onClick={download}
          className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-[11px] font-semibold text-red-700 hover:bg-red-100 dark:text-red-300"
        >
          <Download className="h-3.5 w-3.5" />
          {t('problems.download', { defaultValue: 'Descarcă lista (CSV)' })}
        </button>
      </div>
      <ul className="divide-y divide-slate-100 dark:divide-slate-800">
        {groups.map((g) => {
          const isOpen = open.has(g.code);
          const fields = [...g.fields].map((f) => t(`field.${f}`, { defaultValue: f })).join(', ');
          const rows = showAll.has(g.code) ? g.items : g.items.slice(0, PREVIEW_ROWS);
          return (
            <li key={g.code} className="px-3 py-2 text-xs">
              <button
                type="button"
                onClick={() => setOpen((s) => toggle(s, g.code))}
                aria-expanded={isOpen}
                className="flex w-full items-start gap-2 text-left"
              >
                <ChevronRight className={cn('mt-0.5 h-3.5 w-3.5 shrink-0 transition-transform', isOpen && 'rotate-90')} />
                <span className="flex-1">
                  <span className="font-semibold text-slate-800 dark:text-slate-100">
                    {t(`problems.code.${g.code}.title`, {
                      defaultValue: g.items[0]!.reason,
                      fields,
                    })}
                  </span>
                  <span className="ml-2 rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-semibold text-red-700 dark:bg-red-900/40 dark:text-red-300">
                    {g.items.length}
                  </span>
                  <span className="mt-0.5 block text-slate-600 dark:text-slate-400">
                    {t(`problems.code.${g.code}.hint`, { defaultValue: '', fields })}
                  </span>
                </span>
              </button>
              {isOpen ? (
                <div className="mt-2 max-h-72 overflow-auto rounded border border-slate-100 dark:border-slate-800">
                  <table className="w-full text-[11px]">
                    <tbody>
                      {rows.map((p, i) => (
                        <tr key={`${p.rawIndex}-${i}`} className="border-t border-slate-100 first:border-t-0 dark:border-slate-800">
                          <td className="whitespace-nowrap px-2 py-1 font-semibold text-slate-700 dark:text-slate-200">{label(p)}</td>
                          <td className="px-2 py-1 text-slate-600 dark:text-slate-400">
                            {Object.entries(p.context ?? {}).length > 0 ? (
                              <span className="flex flex-wrap gap-1">
                                {Object.entries(p.context ?? {}).map(([k, v]) => (
                                  <span key={k} className="rounded bg-slate-100 px-1.5 py-0.5 dark:bg-slate-800">
                                    <span className="text-slate-400">{k}:</span> {v}
                                  </span>
                                ))}
                              </span>
                            ) : (
                              p.reason
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {g.items.length > PREVIEW_ROWS && !showAll.has(g.code) ? (
                    <button
                      type="button"
                      onClick={() => setShowAll((s) => toggle(s, g.code))}
                      className="w-full border-t border-slate-100 py-1 text-[11px] font-semibold text-slate-600 hover:bg-slate-50 dark:border-slate-800"
                    >
                      {t('problems.more', { defaultValue: 'Arată toate ({{count}})', count: g.items.length })}
                    </button>
                  ) : null}
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
